import BaseError from "./BaseError";
import Plain from "../types/Plain"
import type {AccessKind} from "@/types/Idea";
import {AxiosError} from "axios";
import {t} from "@/i18n";
import firebase from "firebase";
import FirebaseError = firebase.FirebaseError;


type NotFoundErrorDataType = { collection: string, filter: Plain }
export class NotFoundError extends BaseError {
    data: NotFoundErrorDataType
    constructor(data: { collection: string, filter: Plain }, cause?: Error) {
        super('Not Found', data, cause)
        this.data = data
    }
}

type NotValidErrorDataType = { validation: string, property: string | Array<string | Plain> }
export class NotValidError extends BaseError {
    data: NotValidErrorDataType
    constructor(data: NotValidErrorDataType, cause?: Error) {
        super('Not Valid', data, cause)
        this.data = data
    }
}

/**
 * `formulas`: as que citam a opção, situação ou papel que se quis remover — o atributo, ou a pergunta
 * de acesso (`access`) da coleção — ver citingFormulasMessage.
 */
type DependencyErrorDataType = { dependencies: {idea?: number, instance?: number, property?: number, ideaAlias?: string,
        formulas?: Array<{idea: string, property?: string, access?: AccessKind}>} }
export class DependencyError extends BaseError {
    data: DependencyErrorDataType
    constructor(data: DependencyErrorDataType, cause?: Error) {
        super('Dependency', data, cause)
        this.data = data
    }
}

type NotUniqueErrorDataType = { property: string | Array<string | Plain> }
export class NotUniqueError extends BaseError {
    data: NotUniqueErrorDataType
    constructor(data: NotUniqueErrorDataType, cause?: Error) {
        super('Not Unique', data, cause)
        this.data = data
    }
}

export class CredentialError extends BaseError {
    constructor(cause?: Error) {
        super('Invalid Credentials', undefined, cause)
    }
}

/**
 * O 403 do servidor — e **com o `data`**, que é o que diz QUAL recusa foi esta.
 *
 * O construtor só recebia a causa, e o dado era descartado aqui, duas camadas antes de quem o
 * leria. Com isso, nenhuma das recusas que o servidor distingue pelo `validation` chegava a ser
 * traduzida: ambiente encerrado, papel insuficiente e convite pendente caíam todas em "Erro
 * inesperado". O `saveErrorMessage` existe para traduzi-las, e recebia sempre um objeto vazio.
 *
 * O login por senha escapava porque reconhece pela CLASSE, não pelo dado — e foi isso que
 * escondeu o defeito: a única tela que mostrava a frase certa não dependia do que faltava.
 */
export class AuthorizationError extends BaseError {
    constructor(data?: Plain, cause?: Error) {
        super('Not Authorized', data, cause)
    }
}

export class EmailNotVerifiedError extends BaseError {
    constructor(data: { type: string }, cause?: Error) {
        super('Email not verified.', data, cause)
    }
}

export class ServerError extends BaseError {
    constructor(cause?: Error) {
        super('Server Error',  undefined, cause)
    }
}

/**
 * O servidor recusou o corpo por tamanho. Sem este caso o 413 caía no default e
 * virava "erro inesperado" — era assim que o limite de 100 kB do body parser
 * chegava ao usuário.
 */
export class PayloadTooLargeError extends BaseError {
    constructor(cause?: Error) {
        super(t('error.payload-too-large'), undefined, cause)
    }
}

export function throwErrorForCode(err: AxiosError) {
    const code = err.response?.status
    const data = err.response?.data?.data
    switch (code) {
        case 409: throw new NotUniqueError(data,err)
        case 404: throw new NotFoundError(data,err)
        case 400: throw new NotValidError(data,err)
        case 428: throw new DependencyError(data,err)
        case 413: throw new PayloadTooLargeError(err)
        case 403: {
            if(data?.type === 'EMAIL_NOT_VERIFIED') throw new EmailNotVerifiedError(data, err)
            else throw new AuthorizationError(data, err)
        }
        case 401: throw new CredentialError(err)
        case 500: throw new ServerError(err)
        default: throw err
    }
}


